import { useEffect, useRef, useState } from "react";
import { api, itemImage, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Receipt, Loader2, Check, Camera } from "lucide-react";
import { toast } from "sonner";

const NONE = "__none__";

/** Snap a receipt -> AI reads lines, matches to closet items -> confirm -> prices & dates saved. */
export const ReceiptDialog = ({ open, onOpenChange, onApplied }) => {
  const inputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [date, setDate] = useState("");
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (open) { api.listItems({}).then(setItems).catch(() => {}); }
    else { setReceipt(null); setPreview(null); setDate(""); }
  }, [open]);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setScanning(true);
    try {
      const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
      setPreview(b64);
      const r = await api.scanReceipt(b64);
      setReceipt(r);
      setDate(r.date || "");
      if (!r.lines?.length) toast.info("No clothing lines found on that receipt");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read the receipt");
    } finally {
      setScanning(false);
    }
  };

  const setLine = (i, patch) => setReceipt((r) => ({ ...r, lines: r.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) }));

  const apply = async () => {
    setApplying(true);
    try {
      const r = await api.applyReceipt(receipt.id, {
        date: date || null,
        lines: receipt.lines.map((l) => ({ description: l.description, price: Number(l.price) || 0, match_id: l.match_id || null })),
      });
      toast.success(`Updated ${r.applied} item${r.applied === 1 ? "" : "s"} with what you paid`);
      onApplied?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not apply");
    } finally {
      setApplying(false);
    }
  };

  const matched = receipt?.lines?.filter((l) => l.match_id).length || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="receipt-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Receipt className="h-5 w-5" /> Add a receipt</DialogTitle>
          <DialogDescription>Snap it after shopping — I'll read the prices and attach them to the matching pieces in your closet.</DialogDescription>
        </DialogHeader>

        {!receipt ? (
          <button onClick={() => inputRef.current?.click()} disabled={scanning} className="rounded-[var(--radius)] border-2 border-dashed border-border hover:border-action p-8 text-center transition-colors w-full" data-testid="receipt-dropzone">
            <input ref={inputRef} type="file" accept="image/*,.heic" className="hidden" onChange={pick} data-testid="receipt-file-input" />
            {scanning ? (
              <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
                {preview && <img src={preview} alt="receipt" className="h-24 rounded-lg object-cover opacity-60" />}
                <Loader2 className="h-5 w-5 animate-spin text-action" /> Reading the receipt…
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 text-sm">
                <Camera className="h-7 w-7 text-muted-foreground" />
                <span className="font-medium">Tap to photograph or upload a receipt</span>
                <span className="text-xs text-muted-foreground">Works with store receipts and order confirmations</span>
              </div>
            )}
          </button>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-3 text-sm">
              <div><p className="text-xs text-muted-foreground">Store</p><p className="font-medium" data-testid="receipt-store">{receipt.store || "Unknown"}</p></div>
              <div><p className="text-xs text-muted-foreground">Receipt total</p><p className="font-mono-plex font-medium">{money(receipt.total)}</p></div>
              <div className="ml-auto">
                <p className="text-xs text-muted-foreground">Purchase date</p>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 mt-0.5" data-testid="receipt-date-input" />
              </div>
            </div>

            <div className="rounded-[var(--radius)] border border-border divide-y divide-border" data-testid="receipt-lines">
              {receipt.lines.map((l, i) => {
                const m = items.find((it) => it.id === l.match_id) || l.match;
                return (
                  <div key={i} className="p-3 grid grid-cols-[1fr_auto] sm:grid-cols-[1.2fr_90px_1.4fr] gap-3 items-center" data-testid={`receipt-line-${i}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{l.description}</p>
                      {l.qty > 1 && <p className="text-[11px] text-muted-foreground">× {l.qty}</p>}
                    </div>
                    <Input type="number" step="0.01" value={l.price} onChange={(e) => setLine(i, { price: e.target.value })} className="h-9 font-mono-plex" data-testid={`receipt-line-price-${i}`} />
                    <div className="col-span-2 sm:col-span-1 flex items-center gap-2 min-w-0">
                      {m && <img src={itemImage(m)} alt="" className="h-9 w-9 rounded-lg object-cover shrink-0" />}
                      <Select value={l.match_id || NONE} onValueChange={(v) => setLine(i, { match_id: v === NONE ? null : v })}>
                        <SelectTrigger className="h-9" data-testid={`receipt-line-match-${i}`}><SelectValue placeholder="Match to item" /></SelectTrigger>
                        <SelectContent className="max-h-72">
                          <SelectItem value={NONE}>— Not in my closet —</SelectItem>
                          {items.map((it) => <SelectItem key={it.id} value={it.id}>{it.name}{it.brand ? ` · ${it.brand}` : ""}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-2 justify-between items-center">
              <button onClick={() => { setReceipt(null); setPreview(null); }} className="text-xs text-muted-foreground underline" data-testid="receipt-rescan">Scan a different receipt</button>
              <Button onClick={apply} disabled={applying || matched === 0} className="rounded-full" data-testid="receipt-apply-button">
                {applying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                Save prices to {matched} item{matched === 1 ? "" : "s"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
